/**
 * KarroFin Admin — demo dataset.
 *
 * Built once, deterministically, from a fixed seed so the demo looks identical
 * on every reload and across machines (no Math.random, no Date.now in the
 * generator itself).
 *
 * The shapes here mirror the real backend rows:
 *   user    ~ users.CustomUser + users.UserProfileData
 *   product ~ products.Product + products.ProductEligibilityDetail
 *   offer   ~ loan.Offers            (flag_offer_availed == "clicked")
 *   loan    ~ loan.LoanData          (the application)
 *
 * Coherence rules that the whole UI depends on:
 *   - every offer belongs to exactly one user and one product
 *   - a user's bucket is DERIVED from their offers, never stored
 *   - a disbursed offer implies its loan reached a disbursed precheck_status
 *   - counts shown on the dashboard are sums over these arrays, not constants
 */
import {
  PRODUCT_TYPE,
  OFFER_STATUS,
  OFFER_LOAN_STATUS,
  PRECHECK_STATUS,
  PROFILE_STATUS,
  FUNNEL_STATUS,
  PLATFORM,
  CHANNEL,
  DELIVERY_STATUS,
  WHATSAPP_TEMPLATES,
  REJECTION_REASONS,
  EC1_PROFILE_STATUSES,
} from "../constants";

/* --------------------------------------------------------------- helpers */

/** mulberry32 — small, fast, deterministic PRNG. */
export function makeRng(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rng = makeRng(20260812);

const int = (min, max) => Math.floor(rng() * (max - min + 1)) + min;
const pick = (arr) => arr[Math.floor(rng() * arr.length)];
const chance = (p) => rng() < p;

function pickWeighted(pairs) {
  const total = pairs.reduce((s, p) => s + p[1], 0);
  let r = rng() * total;
  for (const [value, weight] of pairs) {
    r -= weight;
    if (r <= 0) return value;
  }
  return pairs[pairs.length - 1][0];
}

/**
 * The dataset is anchored to a fixed "today" so it is fully deterministic.
 * Screens that show relative ages read this, not the wall clock.
 */
export const TODAY = new Date(2026, 7, 12); // 2026-08-12, month is 0-indexed

function ymd(d) {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function daysAgo(n) {
  const d = new Date(TODAY.getTime());
  d.setDate(d.getDate() - n);
  return d;
}

function isoAgo(n, hour) {
  const d = daysAgo(n);
  d.setHours(hour === undefined ? int(9, 19) : hour, int(0, 59), 0, 0);
  return d.toISOString();
}

/* ------------------------------------------------------------ name pools */

const FIRST_NAMES = [
  "Aarav", "Vivaan", "Aditya", "Vihaan", "Arjun", "Reyansh", "Krishna", "Ishaan",
  "Rahul", "Rohit", "Amit", "Sandeep", "Manish", "Pankaj", "Suresh", "Rajesh",
  "Anita", "Priya", "Sneha", "Pooja", "Kavya", "Divya", "Meera", "Nisha",
  "Shreya", "Ananya", "Ritu", "Swati", "Neha", "Deepika", "Farhan", "Imran",
  "Zoya", "Sana", "Rizwan", "Kabir", "Naveen", "Harish", "Tejesh", "Lakshmi",
];

const LAST_NAMES = [
  "Sharma", "Verma", "Patel", "Reddy", "Nair", "Iyer", "Singh", "Gupta",
  "Mehta", "Joshi", "Desai", "Kulkarni", "Rao", "Chauhan", "Yadav", "Mishra",
  "Bose", "Das", "Ghosh", "Khan", "Pillai", "Menon", "Shetty", "Kapoor",
];

const CITIES = [
  "Mumbai", "Delhi", "Bengaluru", "Hyderabad", "Pune", "Chennai", "Kolkata",
  "Ahmedabad", "Jaipur", "Lucknow", "Indore", "Nagpur", "Bhopal", "Raipur",
];

/* -------------------------------------------------------- UTM value pools */

const UTM_SOURCES = ["google", "facebook", "instagram", "affiliate", "sms_blast", "organic", "youtube"];
const UTM_MEDIUMS = ["cpc", "social", "email", "referral", "display", "organic"];
const UTM_CAMPAIGNS = [
  "pl_aug_salaried", "pl_instant_50k", "bl_msme_growth", "bl_working_capital",
  "retarget_dropoff", "brand_search", "festive_offer", "pl_low_cibil",
];
const UTM_TERMS = ["personal loan", "instant loan", "business loan", "loan for shop", "loan app", ""];
const UTM_CONTENTS = ["banner_a", "banner_b", "video_15s", "carousel", "text_ad", ""];

/* ------------------------------------------------------------ accents */

/**
 * Card accent colours, cycled across the catalogue.
 *
 * The palette exists purely to keep a grid of product cards scannable -- it
 * carries no meaning beyond "these are different products".
 */
const ACCENTS = [
  "#003399", "#00695C", "#4527A0", "#AD1457", "#EF6C00",
  "#1565C0", "#2E7D32", "#5D4037", "#00838F", "#C62828",
];

/* --------------------------------------------------------------- products */

const PRODUCT_SEED = [
  ["Instant Personal Loan", PRODUCT_TYPE.PERSONAL_LOAN],
  ["Salaried Personal Loan", PRODUCT_TYPE.PERSONAL_LOAN],
  ["Express Cash Loan", PRODUCT_TYPE.PERSONAL_LOAN],
  ["Flexi Personal Loan", PRODUCT_TYPE.PERSONAL_LOAN],
  ["Salary Advance Loan", PRODUCT_TYPE.PERSONAL_LOAN],
  ["Digital Personal Loan", PRODUCT_TYPE.PERSONAL_LOAN],
  ["Short-Term Cash Loan", PRODUCT_TYPE.PERSONAL_LOAN],
  ["Pre-Approved Personal Loan", PRODUCT_TYPE.PERSONAL_LOAN],
  ["Business Growth Loan", PRODUCT_TYPE.BUSINESS_LOAN],
  ["Working Capital Loan", PRODUCT_TYPE.BUSINESS_LOAN],
  ["Merchant Business Loan", PRODUCT_TYPE.BUSINESS_LOAN],
];

/**
 * Stand-in for `products.Product.logo`, which is a real ImageField server-side
 * but empty in this environment.
 *
 * A coloured monogram built from the product's initials. It exists so the
 * "show the offer's icon if there is one" path is visible in the demo; real
 * logos take over automatically the moment the API serves them.
 *
 * Two products deliberately get none, so the no-logo fallback is on screen too.
 */
function placeholderLogo(name, color) {
  const initials = name
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">` +
    `<rect width="40" height="40" rx="9" fill="${color}"/>` +
    `<text x="50%" y="50%" dy="0.35em" text-anchor="middle" ` +
    `font-family="Nunito Sans, Arial, sans-serif" font-size="15" ` +
    `font-weight="700" fill="#ffffff">${initials}</text></svg>`;
  return "data:image/svg+xml," + encodeURIComponent(svg);
}

export const products = PRODUCT_SEED.map(([name, type], i) => {
  const min_income = pick([15000, 20000, 25000, 30000]);
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const color = ACCENTS[i % ACCENTS.length];
  return {
    id: i + 1,
    name,
    title: name,
    product_type: type,
    // One product ships disabled so the "switched off" card state is on screen.
    is_active: name !== "Pre-Approved Personal Loan",
    interest_rate: `${int(11, 26)}% - ${int(27, 36)}%`,
    tenure: `${pick([3, 6])} - ${pick([24, 36, 60])} months`,
    loan_upto: pick([100000, 200000, 500000, 1000000, 2000000]),
    processing_fees: `${int(1, 4)}%`,
    priority: int(1, 9),
    flag_recomended: chance(0.25),
    enable_manual_application: chance(0.7),
    web_url: `https://apply.karrofin.com/${slug}`,
    utm_link: `https://track.karrofin.com/${slug}/?lead={LeadID}&mobile={mobile_no}&utm_source=karrofin`,
    color,
    // Two products carry no logo, so the missing-logo fallback is visible too.
    logo: i % 5 === 3 ? null : placeholderLogo(name, color),
    created: ymd(daysAgo(int(120, 800))),
    // ~ products.ProductEligibilityDetail
    eligibility: {
      minimum_age: 21,
      maximum_age: pick([55, 58, 60]),
      minimum_income: min_income,
      maximum_income: null,
      employment_type: type === PRODUCT_TYPE.BUSINESS_LOAN ? "Self Employed" : "Salaried",
      salary_type: type === PRODUCT_TYPE.BUSINESS_LOAN ? null : "Bank Transfer",
      cibil_score: pick([650, 680, 700, 720, 750]),
      pincode_count: int(1200, 9800),
    },
  };
});

const productsById = {};
products.forEach((p) => (productsById[p.id] = p));

/* ------------------------------------------------------------------ users */

const USER_COUNT = 260;

export const users = [];
export const offers = [];
export const loans = [];

let offerSeq = 1;
let loanSeq = 1;

for (let i = 0; i < USER_COUNT; i++) {
  const first = pick(FIRST_NAMES);
  const last = pick(LAST_NAMES);
  const signup_days_ago = int(0, 120);

  // Platform drives whether UTM exists at all: only the website writes UTM.
  // The Android app sends none -- this is a real gap, reproduced faithfully
  // so the UTM screen's "no attribution" segment is honest.
  const platform = pickWeighted([
    [PLATFORM.WEB, 58],
    [PLATFORM.ANDROID, 30],
    [PLATFORM.PARTNER, 8],
    [PLATFORM.WHATSAPP, 4],
  ]);
  const hasUtm = platform === PLATFORM.WEB && chance(0.88);

  const customer_type = pickWeighted([
    [PRODUCT_TYPE.PERSONAL_LOAN, 74],
    [PRODUCT_TYPE.BUSINESS_LOAN, 26],
  ]);

  const user = {
    id: 1000 + i,
    customer_id: "KF" + String(24500 + i),
    name: `${first} ${last}`,
    first_name: first,
    last_name: last,
    mobile: "9" + String(int(100000000, 999999999)),
    email: `${first.toLowerCase()}.${last.toLowerCase()}${int(1, 99)}@example.com`,
    city: pick(CITIES),
    age: int(23, 52),
    monthly_income: pick([18000, 22000, 27000, 32000, 45000, 60000, 85000]),
    cibil_score: chance(0.12) ? null : int(580, 812),
    platform,
    signup_using: pick(["Cashpo", "Google", "Truecaller", "Business Loan Website"]),
    customer_type,
    created: isoAgo(signup_days_ago),
    signed_up_date: ymd(daysAgo(signup_days_ago)),
    // UTM lives on the user row only (users/models.py:118-124)
    utm_source: hasUtm ? pick(UTM_SOURCES) : null,
    utm_medium: hasUtm ? pick(UTM_MEDIUMS) : null,
    utm_campaign: hasUtm ? pick(UTM_CAMPAIGNS) : null,
    utm_term: hasUtm ? pick(UTM_TERMS) || null : null,
    utm_content: hasUtm ? pick(UTM_CONTENTS) || null : null,
    utm_platform: hasUtm ? platform : null,
    lead_partner: platform === PLATFORM.PARTNER ? pick(["Paisabazaar", "BankSathi", "CreditSea"]) : null,
    // lead_source is computed server-side (users/serializers.py:2020):
    // partner name when flag_partner_lead, else "Karrofin".
    get lead_source() {
      return this.lead_partner || "Karrofin";
    },
  };

  /* -- profile / funnel progression ------------------------------------- */
  // Roughly a funnel: most users stall early, a few reach disbursal.
  // Weights are tuned so every bucket and pipeline stage has enough rows to be
  // legible in the UI, while keeping the shape of a real lending funnel
  // (most users stall early; disbursal stays a low single-digit % of offers).
  const journey = pickWeighted([
    ["ec1", 22], // never finished EC1
    ["ec_rejected", 10], // internally rejected
    ["offers_no_click", 18], // offers generated, nothing clicked
    ["offers_some_click", 15],
    ["offers_all_click", 8],
    ["under_process", 12],
    ["disbursed", 15],
  ]);

  if (journey === "ec1") {
    user.profile_status = pick(EC1_PROFILE_STATUSES);
    user.funnel_status = FUNNEL_STATUS.LEAD;
  } else if (journey === "ec_rejected") {
    user.profile_status = PROFILE_STATUS.EC_REJECTED;
    user.funnel_status = FUNNEL_STATUS.INTERNAL_EC;
    user.rejection_reason = pick(REJECTION_REASONS);
  } else {
    user.profile_status = PROFILE_STATUS.LOAN_CREATED;
    user.funnel_status =
      journey === "disbursed" ? FUNNEL_STATUS.DISBURSED : FUNNEL_STATUS.APPROVED;
  }

  user.journey = journey;
  users.push(user);

  /* -- offers ------------------------------------------------------------ */
  // Users who never cleared EC1 or were rejected have no offers at all --
  // that is exactly what makes the "No offer clicked" bucket meaningful.
  if (journey === "ec1" || journey === "ec_rejected") continue;

  // A user only ever sees offers for the product they came in for.
  const eligible = products.filter(
    (p) => p.is_active && p.product_type === customer_type
  );
  if (!eligible.length) continue;

  const offerCount = Math.min(eligible.length, int(2, 5));
  const chosen = [];
  const pool = eligible.slice();
  for (let k = 0; k < offerCount && pool.length; k++) {
    chosen.push(pool.splice(Math.floor(rng() * pool.length), 1)[0]);
  }

  // How many of this user's offers get clicked, per their journey.
  let clickCount;
  if (journey === "offers_no_click") clickCount = 0;
  else if (journey === "offers_some_click") clickCount = int(1, Math.max(1, chosen.length - 1));
  else if (journey === "offers_all_click") clickCount = chosen.length;
  else clickCount = int(1, chosen.length); // under_process / disbursed

  const offer_days_ago = Math.max(0, signup_days_ago - int(0, 3));

  // One application row per user, mirroring loan.LoanData
  const loan = {
    id: loanSeq++,
    user_id: user.id,
    loan_type:
      customer_type === PRODUCT_TYPE.BUSINESS_LOAN
        ? PRODUCT_TYPE.BUSINESS_LOAN
        : PRODUCT_TYPE.PERSONAL_LOAN,
    product_type: customer_type,
    initiated_by: "Karrofin",
    loan_amount: pick([50000, 75000, 100000, 150000, 200000, 300000]),
    created: isoAgo(offer_days_ago),
    precheck_status: PRECHECK_STATUS.APPROVED,
    loan_reject_reason: null,
    disbursal_date: null,
  };

  chosen.forEach((product, idx) => {
    const clicked = idx < clickCount;
    const offer = {
      id: offerSeq++,
      user_id: user.id,
      loan_id: loan.id,
      product_id: product.id,
      title: product.name,
      logo: product.logo,
      product_type: product.product_type,
      interest_rate: product.interest_rate,
      tenure: product.tenure,
      loan_upto: product.loan_upto,
      processing_fees: product.processing_fees,
      is_offer_available: true,
      offer_status: OFFER_STATUS.APPROVED,
      // The only "click" signal that exists server-side.
      flag_offer_availed: clicked,
      // Leads.created is the de-facto first-click timestamp.
      clicked_at: clicked ? isoAgo(Math.max(0, offer_days_ago - int(0, 2))) : null,
      loan_status: OFFER_LOAN_STATUS.APPLICATION_PENDING,
      loan_sub_status: null,
      flag_disbursed: false,
      disbursal_date: null,
      disbursal_amount: null,
      remark: null,
      created: isoAgo(offer_days_ago),
      priority: product.priority,
      flag_recomended: product.flag_recomended,
      redirect_url: product.utm_link
        .replace("{LeadID}", String(offerSeq))
        .replace("{mobile_no}", user.mobile),
    };

    // Progress the clicked offers according to the user's journey.
    if (clicked) {
      if (journey === "under_process") {
        offer.loan_status = OFFER_LOAN_STATUS.APPLICATION_PENDING;
        offer.loan_sub_status = PRECHECK_STATUS.UNDER_PROCESS;
      } else if (journey === "disbursed") {
        // Only ONE offer disburses; the rest stay live, which is exactly the
        // behaviour the scope calls out (a user keeps seeing other offers).
        if (idx === 0) {
          offer.loan_status = OFFER_LOAN_STATUS.DISBURSED;
          offer.flag_disbursed = true;
          offer.disbursal_date = ymd(daysAgo(Math.max(0, offer_days_ago - int(3, 12))));
          offer.disbursal_amount = pick([40000, 60000, 90000, 120000, 180000]);
          loan.disbursal_date = offer.disbursal_date;
          loan.precheck_status = PRECHECK_STATUS.DISBURSED_BY_NBFC;
        } else if (chance(0.35)) {
          offer.loan_status = OFFER_LOAN_STATUS.REJECTED;
          offer.remark = pick(REJECTION_REASONS);
        }
      } else if (chance(0.22)) {
        offer.loan_status = OFFER_LOAN_STATUS.REJECTED;
        offer.remark = pick(REJECTION_REASONS);
      }
    }

    offers.push(offer);
  });

  // Reflect the aggregate back onto the application row.
  const userOffers = offers.filter((o) => o.user_id === user.id);
  if (journey === "under_process") {
    loan.precheck_status = PRECHECK_STATUS.UNDER_PROCESS;
  } else if (
    userOffers.length &&
    userOffers.every((o) => o.loan_status === OFFER_LOAN_STATUS.REJECTED)
  ) {
    loan.precheck_status = PRECHECK_STATUS.REJECTED_BY_NBFC;
    loan.loan_reject_reason = pick(REJECTION_REASONS);
  } else if (journey !== "disbursed") {
    loan.precheck_status = PRECHECK_STATUS.OFFER_GENERATED;
  }

  loans.push(loan);
}

/* --------------------------------------------------- curated showcase leads
 *
 * The generated users above cover the buckets statistically, but finding one
 * that demonstrates a specific behaviour means hunting. These six have FIXED
 * ids (900-905) so they can be linked to directly, and each one is built to
 * show exactly one thing that is otherwise hard to spot.
 *
 * They are appended, not substituted -- every count above still holds.
 */
const SHOWCASE = [
  {
    id: 900,
    name: "Ananya Krishnan",
    demonstrates:
      "One offer disbursed and another rejected, yet two more remain live " +
      "and clickable — the core rule in the KarroFin scope",
    city: "Bengaluru",
    platform: PLATFORM.WEB,
    customer_type: PRODUCT_TYPE.PERSONAL_LOAN,
    profile_status: PROFILE_STATUS.LOAN_CREATED,
    funnel_status: FUNNEL_STATUS.DISBURSED,
    income: 68000,
    cibil: 771,
    utm: { source: "google", medium: "cpc", campaign: "pl_aug_salaried", term: "instant loan", content: "banner_a" },
    days: 24,
    offers: [
      { product: "Instant Personal Loan", clicked: true, outcome: "disbursed", amount: 150000 },
      { product: "Salaried Personal Loan", clicked: true, outcome: "rejected", reason: "Existing loan on record" },
      { product: "Salary Advance Loan", clicked: false, outcome: "live" },
      { product: "Digital Personal Loan", clicked: false, outcome: "live" },
    ],
  },
  {
    id: 901,
    name: "Rohit Malhotra",
    demonstrates: "All offers clicked, every one still under process",
    city: "Pune",
    platform: PLATFORM.WEB,
    customer_type: PRODUCT_TYPE.PERSONAL_LOAN,
    profile_status: PROFILE_STATUS.LOAN_CREATED,
    funnel_status: FUNNEL_STATUS.APPROVED,
    income: 42000,
    cibil: 702,
    utm: { source: "facebook", medium: "social", campaign: "retarget_dropoff", term: "", content: "video_15s" },
    days: 9,
    offers: [
      { product: "Express Cash Loan", clicked: true, outcome: "under_process" },
      { product: "Flexi Personal Loan", clicked: true, outcome: "under_process" },
      { product: "Short-Term Cash Loan", clicked: true, outcome: "under_process" },
    ],
  },
  {
    id: 902,
    name: "Sana Qureshi",
    demonstrates: "Offers generated but none clicked — the re-engagement target",
    city: "Hyderabad",
    platform: PLATFORM.WEB,
    customer_type: PRODUCT_TYPE.PERSONAL_LOAN,
    profile_status: PROFILE_STATUS.LOAN_CREATED,
    funnel_status: FUNNEL_STATUS.APPROVED,
    income: 31000,
    cibil: 688,
    utm: { source: "instagram", medium: "social", campaign: "festive_offer", term: "", content: "carousel" },
    days: 16,
    offers: [
      { product: "Salary Advance Loan", clicked: false, outcome: "live" },
      { product: "Digital Personal Loan", clicked: false, outcome: "live" },
      { product: "Flexi Personal Loan", clicked: false, outcome: "live" },
    ],
  },
  {
    id: 903,
    name: "Vikram Shetty",
    demonstrates: "EC1 Pending — signed up but never finished eligibility, so no offers exist",
    city: "Mumbai",
    platform: PLATFORM.WEB,
    customer_type: PRODUCT_TYPE.PERSONAL_LOAN,
    profile_status: PROFILE_STATUS.INITIAL,
    funnel_status: FUNNEL_STATUS.LEAD,
    income: 27000,
    cibil: null,
    utm: { source: "sms_blast", medium: "email", campaign: "brand_search", term: "", content: "" },
    days: 3,
    offers: [],
  },
  {
    id: 904,
    name: "Imran Sheikh",
    demonstrates: "Android signup — reached offers without ever completing a profile",
    city: "Lucknow",
    platform: PLATFORM.ANDROID,
    customer_type: PRODUCT_TYPE.PERSONAL_LOAN,
    profile_status: PROFILE_STATUS.LOAN_CREATED,
    funnel_status: FUNNEL_STATUS.APPROVED,
    income: 38000,
    cibil: 715,
    utm: null,
    days: 12,
    offers: [
      { product: "Instant Personal Loan", clicked: true, outcome: "live" },
      { product: "Pre-Approved Personal Loan", clicked: false, outcome: "live" },
    ],
  },
  {
    id: 905,
    name: "Deepa Nair",
    demonstrates: "Business Loan lead, disbursed — use the BL switch to isolate her",
    city: "Kochi",
    platform: PLATFORM.WEB,
    customer_type: PRODUCT_TYPE.BUSINESS_LOAN,
    profile_status: PROFILE_STATUS.LOAN_CREATED,
    funnel_status: FUNNEL_STATUS.DISBURSED,
    income: 95000,
    cibil: 758,
    utm: { source: "affiliate", medium: "referral", campaign: "bl_msme_growth", term: "business loan", content: "text_ad" },
    days: 31,
    offers: [
      { product: "Merchant Business Loan", clicked: true, outcome: "disbursed", amount: 400000 },
      { product: "Working Capital Loan", clicked: true, outcome: "rejected", reason: "Business age below minimum" },
      { product: "Business Growth Loan", clicked: false, outcome: "live" },
    ],
  },
];

const productByName = {};
products.forEach((p) => (productByName[p.name] = p));

SHOWCASE.forEach((spec) => {
  const [first, last] = spec.name.split(" ");
  const user = {
    id: spec.id,
    customer_id: "KF" + spec.id,
    name: spec.name,
    first_name: first,
    last_name: last,
    mobile: "98" + String(10000000 + spec.id),
    email: `${first.toLowerCase()}.${last.toLowerCase()}@example.com`,
    city: spec.city,
    age: 29 + (spec.id % 9),
    monthly_income: spec.income,
    cibil_score: spec.cibil,
    platform: spec.platform,
    signup_using: "Cashpo",
    customer_type: spec.customer_type,
    // These two drive the EC1 bucket and the detail timeline -- omitting them
    // silently dropped the EC1 showcase lead out of its own bucket.
    profile_status: spec.profile_status,
    funnel_status: spec.funnel_status,
    created: isoAgo(spec.days, 11),
    signed_up_date: ymd(daysAgo(spec.days)),
    utm_source: spec.utm ? spec.utm.source : null,
    utm_medium: spec.utm ? spec.utm.medium : null,
    utm_campaign: spec.utm ? spec.utm.campaign : null,
    utm_term: spec.utm && spec.utm.term ? spec.utm.term : null,
    utm_content: spec.utm && spec.utm.content ? spec.utm.content : null,
    utm_platform: spec.utm ? spec.platform : null,
    lead_partner: null,
    // Surfaced in the UI so it is obvious why this record exists.
    showcase: spec.demonstrates,
  };
  users.push(user);

  if (!spec.offers.length) return;

  const loan = {
    id: loanSeq++,
    user_id: user.id,
    loan_type:
      spec.customer_type === PRODUCT_TYPE.BUSINESS_LOAN
        ? PRODUCT_TYPE.BUSINESS_LOAN
        : PRODUCT_TYPE.PERSONAL_LOAN,
    product_type: spec.customer_type,
    initiated_by: "Karrofin",
    loan_amount: spec.customer_type === PRODUCT_TYPE.BUSINESS_LOAN ? 500000 : 200000,
    created: isoAgo(spec.days, 12),
    precheck_status: PRECHECK_STATUS.OFFER_GENERATED,
    loan_reject_reason: null,
    disbursal_date: null,
  };

  spec.offers.forEach((spec_offer, idx) => {
    const product = productByName[spec_offer.product];
    if (!product) return;
    const offer = {
      id: offerSeq++,
      user_id: user.id,
      loan_id: loan.id,
      product_id: product.id,
      title: product.name,
      logo: product.logo,
      product_type: product.product_type,
      interest_rate: product.interest_rate,
      tenure: product.tenure,
      loan_upto: product.loan_upto,
      processing_fees: product.processing_fees,
      is_offer_available: true,
      offer_status: OFFER_STATUS.APPROVED,
      flag_offer_availed: !!spec_offer.clicked,
      clicked_at: spec_offer.clicked ? isoAgo(Math.max(0, spec.days - 1 - idx), 15) : null,
      loan_status: OFFER_LOAN_STATUS.APPLICATION_PENDING,
      loan_sub_status: null,
      flag_disbursed: false,
      disbursal_date: null,
      disbursal_amount: null,
      remark: null,
      created: isoAgo(spec.days, 12),
      priority: product.priority,
      flag_recomended: product.flag_recomended,
      redirect_url: product.utm_link
        .replace("{LeadID}", String(offerSeq))
        .replace("{mobile_no}", user.mobile),
    };

    if (spec_offer.outcome === "disbursed") {
      offer.loan_status = OFFER_LOAN_STATUS.DISBURSED;
      offer.flag_disbursed = true;
      offer.disbursal_date = ymd(daysAgo(Math.max(0, spec.days - 6)));
      offer.disbursal_amount = spec_offer.amount;
      loan.disbursal_date = offer.disbursal_date;
      loan.precheck_status = PRECHECK_STATUS.DISBURSED_BY_NBFC;
    } else if (spec_offer.outcome === "rejected") {
      offer.loan_status = OFFER_LOAN_STATUS.REJECTED;
      offer.remark = spec_offer.reason;
    } else if (spec_offer.outcome === "under_process") {
      offer.loan_sub_status = PRECHECK_STATUS.UNDER_PROCESS;
      loan.precheck_status = PRECHECK_STATUS.UNDER_PROCESS;
    }

    offers.push(offer);
  });

  loans.push(loan);
});

/* ------------------------------------------------------- derived indexes */

const offersByUser = {};
offers.forEach((o) => {
  (offersByUser[o.user_id] = offersByUser[o.user_id] || []).push(o);
});

const loanByUser = {};
loans.forEach((l) => (loanByUser[l.user_id] = l));

export function offersFor(userId) {
  return offersByUser[userId] || [];
}

export function loanFor(userId) {
  return loanByUser[userId] || null;
}

/**
 * Buckets a user belongs to. A user may legitimately be in several at once
 * (e.g. "All offers clicked" AND "Disbursed") -- the scope calls this out and
 * the UI states it rather than forcing exclusivity.
 */
export function bucketsFor(user) {
  const mine = offersFor(user.id);
  const out = [];

  if (EC1_PROFILE_STATUSES.indexOf(user.profile_status) !== -1) out.push("EC1 Pending");

  if (mine.length) {
    const clicked = mine.filter((o) => o.flag_offer_availed).length;
    if (clicked === 0) out.push("No offer clicked");
    else if (clicked === mine.length) out.push("All offers clicked");
    else out.push("Some offers clicked");

    if (mine.some((o) => o.flag_disbursed)) out.push("Disbursed");
    if (mine.some((o) => o.loan_sub_status === PRECHECK_STATUS.UNDER_PROCESS))
      out.push("Under Process");
    if (mine.length && mine.every((o) => o.loan_status === OFFER_LOAN_STATUS.REJECTED))
      out.push("Rejected");
  }

  if (user.profile_status === PROFILE_STATUS.EC_REJECTED) out.push("Rejected");

  return out;
}

/** Enriched user row used by the lead list and lead detail screens. */
export function enrich(user) {
  const mine = offersFor(user.id);
  const loan = loanFor(user.id);
  const clicked = mine.filter((o) => o.flag_offer_availed);
  const disbursedOffer = mine.find((o) => o.flag_disbursed);
  return {
    ...user,
    lead_source: user.lead_partner || "Karrofin",
    buckets: bucketsFor(user),
    offer_count: mine.length,
    clicked_count: clicked.length,
    disbursed_count: mine.filter((o) => o.flag_disbursed).length,
    rejected_count: mine.filter((o) => o.loan_status === OFFER_LOAN_STATUS.REJECTED).length,
    precheck_status: loan ? loan.precheck_status : null,
    loan_amount: loan ? loan.loan_amount : null,
    disbursal_amount: disbursedOffer ? disbursedOffer.disbursal_amount : null,
    disbursal_date: disbursedOffer ? disbursedOffer.disbursal_date : null,
    last_activity: mine.reduce(
      (acc, o) => (o.clicked_at && o.clicked_at > acc ? o.clicked_at : acc),
      user.created
    ),
  };
}

/* ------------------------------------------ operational fields (2nd pass) */

/**
 * Fields the Offer Management filters need, added AFTER the main generator.
 *
 * Deliberately a second pass on its own random stream. `rng` above is one
 * shared sequence: a single extra draw inside the user loop shifts every value
 * that follows it, so names, cities, journeys, offers and amounts would all
 * change and every count on every KarroFin screen would move for reasons that
 * have nothing to do with this change.
 */
const rng2 = makeRng(20260814);
const int2 = (min, max) => Math.floor(rng2() * (max - min + 1)) + min;
const pick2 = (arr) => arr[Math.floor(rng2() * arr.length)];
const chance2 = (p) => rng2() < p;

// Derived, never drawn: a random state would contradict the city next to it.
const CITY_STATE = {
  Mumbai: "Maharashtra", Pune: "Maharashtra", Nagpur: "Maharashtra",
  Delhi: "Delhi", Bengaluru: "Karnataka", Hyderabad: "Telangana",
  Chennai: "Tamil Nadu", Kolkata: "West Bengal", Ahmedabad: "Gujarat",
  Jaipur: "Rajasthan", Lucknow: "Uttar Pradesh", Indore: "Madhya Pradesh",
  Bhopal: "Madhya Pradesh", Raipur: "Chhattisgarh", Kochi: "Kerala",
};

const AGENTS = [
  { id: 1, name: "Priya Raghavan" },
  { id: 2, name: "Sunil Bhatia" },
  { id: 3, name: "Farah Ansari" },
  { id: 4, name: "Devendra Rao" },
  { id: 5, name: "Meghna Sinha" },
];

const DOC_STATUSES = ["Not Requested", "Requested", "Uploaded"];
const PERFIOS_STATUSES = ["Perfios Only", "Non Perfios", "Unassigned"];

/**
 * Profile bands for User Management.
 *
 * Ranges rather than a pair of number boxes per field: "everyone earning
 * 35-60k" is one question, and two inputs to ask it is two chances to get it
 * half-set. `max_value: null` means open-ended.
 */
const AGE_BANDS = [
  { id: 1, name: "18 - 25", min_value: 18, max_value: 25 },
  { id: 2, name: "26 - 35", min_value: 26, max_value: 35 },
  { id: 3, name: "36 - 45", min_value: 36, max_value: 45 },
  { id: 4, name: "46 and above", min_value: 46, max_value: null },
];

const SALARY_BANDS = [
  { id: 1, name: "Under 20k", min_value: 0, max_value: 19999 },
  { id: 2, name: "20k - 35k", min_value: 20000, max_value: 35000 },
  { id: 3, name: "35k - 60k", min_value: 35001, max_value: 60000 },
  { id: 4, name: "Above 60k", min_value: 60001, max_value: null },
];

// Days ago, not amounts: min_value/max_value are how far back the boundary
// sits, so "Last 7 days" is 0 to 7 and "Over 90 days ago" is 91 to open-ended.
// The server does the subtraction against its own clock -- a browser in another
// timezone must not be able to shift where the boundary falls.
const ACTIVITY_BANDS = [
  { id: 1, name: "Today", min_value: 0, max_value: 0 },
  { id: 2, name: "Last 7 days", min_value: 0, max_value: 7 },
  { id: 3, name: "Last 30 days", min_value: 0, max_value: 30 },
  { id: 4, name: "Last 90 days", min_value: 0, max_value: 90 },
  { id: 5, name: "Over 90 days ago", min_value: 91, max_value: null },
];

// "No score" is its own band: a missing CIBIL is a meaningful state, and a
// range filter would silently drop those users instead of finding them.
const CIBIL_BANDS = [
  { id: 1, name: "No score", min_value: null, max_value: null, no_score: true },
  { id: 2, name: "Below 650", min_value: 0, max_value: 649 },
  { id: 3, name: "650 - 699", min_value: 650, max_value: 699 },
  { id: 4, name: "700 - 749", min_value: 700, max_value: 749 },
  { id: 5, name: "750 and above", min_value: 750, max_value: null },
];


/** Bands for the Products filters. `max_value: null` means open-ended. */
const TENURE_BANDS = [
  { id: 1, name: "Up to 12 months", min_value: null, max_value: 12 },
  { id: 2, name: "13 - 36 months", min_value: 13, max_value: 36 },
  { id: 3, name: "37 - 60 months", min_value: 37, max_value: 60 },
  { id: 4, name: "Over 60 months", min_value: 61, max_value: null },
];

const LOAN_UPTO_BANDS = [
  { id: 1, name: "Under 2 Lakh", min_value: null, max_value: 199999 },
  { id: 2, name: "2 - 5 Lakh", min_value: 200000, max_value: 500000 },
  { id: 3, name: "5 - 10 Lakh", min_value: 500001, max_value: 1000000 },
  { id: 4, name: "Above 10 Lakh", min_value: 1000001, max_value: null },
];

const PRIORITY_BANDS = [
  { id: 1, name: "1 - 5", min_value: 1, max_value: 5 },
  { id: 2, name: "6 - 10", min_value: 6, max_value: 10 },
  { id: 3, name: "11 - 15", min_value: 11, max_value: 15 },
  { id: 4, name: "16 and above", min_value: 16, max_value: null },
];

/** Mirrors master/get_count_dropdown/ so the live swap needs no adapter. */
const AVAILED_OFFER_COUNTS = [
  { id: 1, name: "None availed", min_value: 0, max_value: 0 },
  { id: 2, name: "1 offer", min_value: 1, max_value: 1 },
  { id: 3, name: "2 - 3 offers", min_value: 2, max_value: 3 },
  { id: 4, name: "4 or more", min_value: 4, max_value: null },
];

users.forEach((user) => {
  user.state = CITY_STATE[user.city] || null;

  // Only someone with an application has anything to assign, document or
  // run through Perfios -- a user who never finished eligibility has no file.
  const hasLoan = !!loanFor(user.id);

  const agent = hasLoan && chance2(0.62) ? pick2(AGENTS) : null;
  user.agent_id = agent ? agent.id : null;
  user.agent_name = agent ? agent.name : null;
  user.is_assigned = !!agent;

  // Two slots rather than one flat status, which is what makes the legacy
  // "Both -> Partial Uploaded" option a state that can actually occur.
  user.documents = hasLoan
    ? { address_proof: pick2(DOC_STATUSES), bank_statement: pick2(DOC_STATUSES) }
    : { address_proof: "Not Requested", bank_statement: "Not Requested" };

  user.perfios_status = hasLoan
    ? chance2(0.7)
      ? pick2(["Perfios Only", "Non Perfios"])
      : "Unassigned"
    : null;

  // Negative days are future dates -- a follow-up list that is entirely in the
  // past looks broken.
  user.follow_up_date =
    hasLoan && chance2(0.34) ? ymd(daysAgo(int2(-21, 30))) : null;
});

/** Resolved document status, so page and query layer cannot disagree. */
export function docStatusFor(user, type) {
  const d = (user && user.documents) || {};
  if (type !== "both") return d[type] || "Not Requested";
  const vals = [d.address_proof, d.bank_statement];
  if (vals.every((v) => v === "Uploaded")) return "Uploaded";
  if (vals.some((v) => v === "Uploaded")) return "Partial Uploaded";
  if (vals.some((v) => v === "Requested")) return "Requested";
  return "Not Requested";
}

/**
 * Dropdown options, built from the data that is actually present -- so no
 * filter ever offers a value that matches zero rows.
 */
export const filterOptions = {
  cities: Array.from(new Set(users.map((u) => u.city).filter(Boolean))).sort(),
  states: Array.from(new Set(users.map((u) => u.state).filter(Boolean))).sort(),
  agents: AGENTS.slice(),
  agent_types: [
    { text: "Assigned", value: true },
    { text: "Unassigned", value: false },
  ],
  lead_partners: Array.from(
    new Set(users.map((u) => u.lead_source).filter(Boolean))
  ).sort(),
  document_types: [
    { text: "Rent Address Proof", value: "address_proof" },
    { text: "Bank Statement", value: "bank_statement" },
    { text: "Both", value: "both" },
  ],
  // "Partial Uploaded" is appended by the page when Document type is "both".
  doc_statuses: DOC_STATUSES.slice(),
  // KYC on User Management. Enum values off kyc.models.UserKycInfo rather than
  // data, so they are listed here like document_types above instead of being
  // fetched: there is no endpoint that would tell us anything the model does
  // not. The three documents are the three columns the filter matches; leaving
  // the document unset matches the status on ANY of them.
  kyc_steps: [
    { text: "Any document", value: null },
    { text: "Aadhaar", value: "aadhaar" },
    { text: "PAN", value: "pan" },
    { text: "Selfie", value: "selfie" },
  ],
  kyc_statuses: [
    { text: "Pending", value: "Pending" },
    { text: "Under Review", value: "Under Review" },
    { text: "Success", value: "Success" },
    { text: "Failed", value: "Failed" },
  ],
  perfios_statuses: PERFIOS_STATUSES.slice(),
  availed_offer_counts: AVAILED_OFFER_COUNTS.slice(),
  age_bands: AGE_BANDS.slice(),
  salary_bands: SALARY_BANDS.slice(),
  activity_bands: ACTIVITY_BANDS.slice(),
  cibil_bands: CIBIL_BANDS.slice(),
  platforms: Object.values(PLATFORM),
  tenure_bands: TENURE_BANDS.slice(),
  loan_upto_bands: LOAN_UPTO_BANDS.slice(),
  priority_bands: PRIORITY_BANDS.slice(),
};

export const enrichedUsers = users.map(enrich);

// Offer Management runs up to 18 predicates per row; a linear find per offer
// turns that into O(offers x users) on every keystroke.
const userIndex = {};
enrichedUsers.forEach((u) => (userIndex[u.id] = u));
export function userById(id) {
  return userIndex[id] || null;
}

/* --------------------------------------------------- notification history */

const NOTIFICATION_TITLES = {
  Push: ["Your offer is waiting", "Complete your application", "You're pre-approved"],
  Whatsapp: WHATSAPP_TEMPLATES.map((t) => t.label),
  SMS: ["Offer expiring soon", "Loan pre-approved", "Complete KYC"],
  Email: ["Your KarroFin offer", "Application update", "Disbursal confirmation"],
};

export const notificationHistory = [];
for (let i = 0; i < 180; i++) {
  const user = pick(enrichedUsers);
  const channel = pickWeighted([
    [CHANNEL.WHATSAPP, 44],
    [CHANNEL.PUSH, 28],
    [CHANNEL.SMS, 18],
    [CHANNEL.EMAIL, 10],
  ]);
  const status = pickWeighted([
    [DELIVERY_STATUS.DELIVERED, 42],
    [DELIVERY_STATUS.READ, 22],
    [DELIVERY_STATUS.SENT, 16],
    [DELIVERY_STATUS.LINK_CLICKED, 9],
    [DELIVERY_STATUS.FAILED, 7],
    [DELIVERY_STATUS.PENDING, 4],
  ]);
  notificationHistory.push({
    id: i + 1,
    user_id: user.id,
    name: user.name,
    mobile: user.mobile,
    channel,
    title: pick(NOTIFICATION_TITLES[channel]),
    template:
      channel === CHANNEL.WHATSAPP ? pick(WHATSAPP_TEMPLATES).name : null,
    status,
    contact: channel === CHANNEL.EMAIL ? user.email : user.mobile,
    sent_at: isoAgo(int(0, 45)),
    trigger: chance(0.55) ? pick(["offer_not_clicked", "offer_clicked", "disbursed", "rejected"]) : "manual",
  });
}
// Guarantee the showcase leads have a notification trail, so their detail
// pages demonstrate the history panel instead of an empty state.
SHOWCASE.forEach((spec, i) => {
  const user = enrichedUsers.find((u) => u.id === spec.id);
  if (!user) return;
  const plan = [
    { channel: CHANNEL.WHATSAPP, title: "Welcome", template: "karrofin_welcome", status: DELIVERY_STATUS.READ, trigger: "manual", ago: spec.days },
    { channel: CHANNEL.SMS, title: "Loan pre-approved", template: null, status: DELIVERY_STATUS.DELIVERED, trigger: "new_eligible_offer", ago: Math.max(0, spec.days - 1) },
    {
      channel: CHANNEL.WHATSAPP,
      title: spec.offers.some((o) => o.clicked) ? "Product Benefits" : "Offer Reminder",
      template: spec.offers.some((o) => o.clicked) ? "karrofin_product_benefits" : "karofin_offer_reminder",
      status: DELIVERY_STATUS.LINK_CLICKED,
      trigger: spec.offers.some((o) => o.clicked) ? "offer_clicked" : "offer_not_clicked",
      ago: Math.max(0, spec.days - 3),
    },
  ];
  if (spec.offers.some((o) => o.outcome === "disbursed")) {
    plan.push({ channel: CHANNEL.WHATSAPP, title: "Disbursal Confirmation", template: "karrofin_disbursal_confirmation", status: DELIVERY_STATUS.READ, trigger: "disbursed", ago: Math.max(0, spec.days - 6) });
  }
  plan.forEach((n, j) => {
    notificationHistory.push({
      id: 100000 + i * 10 + j,
      user_id: user.id,
      name: user.name,
      mobile: user.mobile,
      channel: n.channel,
      title: n.title,
      template: n.template,
      status: n.status,
      contact: n.channel === CHANNEL.EMAIL ? user.email : user.mobile,
      sent_at: isoAgo(n.ago, 10 + j),
      trigger: n.trigger,
    });
  });
});

notificationHistory.sort((a, b) => (a.sent_at < b.sent_at ? 1 : -1));

/* ---------------------------------------------------------- utm dropdown */

export const utmOptions = {
  utm_sources: UTM_SOURCES.slice(),
  utm_mediums: UTM_MEDIUMS.slice(),
  utm_campaigns: UTM_CAMPAIGNS.slice(),
  utm_terms: UTM_TERMS.filter(Boolean),
  utm_contents: UTM_CONTENTS.filter(Boolean),
  utm_platforms: Object.values(PLATFORM),
};

export default {
  TODAY,
  users,
  enrichedUsers,
  products,
  offers,
  loans,
  notificationHistory,
  utmOptions,
  offersFor,
  loanFor,
  filterOptions,
  docStatusFor,
  userById,
  bucketsFor,
  enrich,
};
